import db, { bulkUpsert } from './localDb';
import { queueOfflineAction } from './syncService';

/**
 * offlineApi — Local Dexie read/write operations per collection.
 *
 * READ functions  → return data from IndexedDB
 * WRITE functions → write optimistically to IndexedDB + queue for sync
 * CACHE functions → called after a successful online API response to keep Dexie fresh
 */

// ── User ─────────────────────────────────────────────────
export const offlineUserAPI = {
  async getProfile() {
    return db.users.toCollection().first();
  },
  async getFullProfile() {
    const user = await db.users.toCollection().first();
    return user ? { user, stats: user._stats || {}, badges: user._badges || [] } : null;
  },
  async checkIn() {
    await queueOfflineAction({ type: 'POST', endpoint: '/users/checkin' });
    const user = await db.users.toCollection().first();
    if (user) {
      await db.users.update(user.id, {
        currentStreak: (user.currentStreak || 0) + 1,
        points: (user.points || 0) + 10,
        lastCheckIn: new Date().toISOString(),
      });
    }
    return { message: 'Check-in enregistre (hors ligne)', pointsEarned: 10 };
  },
  async updateProfile(data) {
    await queueOfflineAction({ type: 'PUT', endpoint: '/users/profile', body: data });
    const user = await db.users.toCollection().first();
    if (user) await db.users.update(user.id, data);
    return { message: 'Profil mis a jour (hors ligne)' };
  },
  // CACHE: store user from API response
  async cacheProfile(userData) {
    if (userData?.id) await db.users.put(userData);
  },
  async cacheFullProfile(data) {
    if (data?.user?.id) await db.users.put({ ...data.user, _stats: data.stats, _badges: data.badges });
  },
};

// ── Modules ──────────────────────────────────────────────
export const offlineModulesAPI = {
  async getAll() {
    return db.modules.toArray();
  },
  async getById(id) {
    return db.modules.get(id);
  },
  async getLessons(moduleId) {
    return db.lessons.where('moduleId').equals(moduleId).sortBy('order');
  },
  async getQuiz(moduleId) {
    return db.quizzes.where('moduleId').equals(moduleId).first();
  },
  async getLessonQuiz(lessonId) {
    return db.quizzes.where('lessonId').equals(lessonId).first();
  },
  // CACHE
  async cacheModules(modules) { if (modules?.length) await bulkUpsert(db.modules, modules); },
  async cacheModule(mod) { if (mod?.id) await db.modules.put(mod); },
  async cacheLessons(lessons) { if (lessons?.length) await bulkUpsert(db.lessons, lessons); },
  async cacheQuiz(quiz) { if (quiz?.id) await db.quizzes.put(quiz); },
};

// ── Lessons ──────────────────────────────────────────────
export const offlineLessonsAPI = {
  async getById(id) {
    return db.lessons.get(id);
  },
  async complete(id) {
    await queueOfflineAction({ type: 'POST', endpoint: `/lessons/${id}/complete` });
    const lesson = await db.lessons.get(id);
    if (lesson) await db.lessons.update(id, { completed: true });
    return { message: 'Lecon completee (hors ligne)', progress: 0, pointsEarned: 50 };
  },
  async cacheLesson(lesson) { if (lesson?.id) await db.lessons.put(lesson); },
};

// ── Quiz ─────────────────────────────────────────────────
export const offlineQuizAPI = {
  async submit(quizId, answers) {
    await queueOfflineAction({ type: 'POST', endpoint: `/quizzes/${quizId}/submit`, body: { answers } });
    await db.quizSubmissions.put({
      id: `local_${Date.now()}`,
      quizId,
      answers,
      score: 0,
      passed: false,
      _pendingSync: true,
      completedAt: new Date().toISOString(),
    });
    return { message: 'Quiz soumis (hors ligne)', score: 0, passed: false, _offline: true };
  },
};

// ── Cycle ────────────────────────────────────────────────
export const offlineCycleAPI = {
  async get() {
    return db.cycles.toCollection().first();
  },
  async create(data) {
    const now = new Date().toISOString();
    await queueOfflineAction({ type: 'POST', endpoint: '/cycle', body: data });
    const id = `local_${Date.now()}`;
    await db.cycles.put({ id, ...data, _pendingSync: true, updatedAt: now });
    return { cycle: { id, ...data }, predictions: [] };
  },
  async update(data) {
    const now = new Date().toISOString();
    await queueOfflineAction({ type: 'PUT', endpoint: '/cycle', body: data });
    const existing = await db.cycles.toCollection().first();
    if (existing) await db.cycles.update(existing.id, { ...data, updatedAt: now });
    return { cycle: { ...existing, ...data }, predictions: [] };
  },
  async getHistory() {
    return db.cycleHistory.orderBy('periodStart').reverse().toArray();
  },
  async recordPeriod(data) {
    const now = new Date().toISOString();
    await queueOfflineAction({ type: 'POST', endpoint: '/cycle/history/record', body: data });
    const id = `local_${Date.now()}`;
    await db.cycleHistory.put({ id, ...data, _pendingSync: true, updatedAt: now });
    return { message: 'Periode enregistree (hors ligne)', id };
  },
  async deleteRecord(id) {
    await queueOfflineAction({ type: 'DELETE', endpoint: `/cycle/history/${id}` });
    await db.cycleHistory.delete(id);
    return { message: 'Supprime (hors ligne)' };
  },
  async updateRecord(id, data) {
    const now = new Date().toISOString();
    await queueOfflineAction({ type: 'PUT', endpoint: `/cycle/history/${id}`, body: data });
    await db.cycleHistory.update(id, { ...data, updatedAt: now });
    return { message: 'Mis a jour (hors ligne)' };
  },
  async getCalendarData(year, month) {
    const history = await db.cycleHistory.toArray();
    const events = [];
    for (const record of history) {
      if (record.periodStart) {
        const start = new Date(record.periodStart);
        for (let i = 0; i < 5; i++) {
          const d = new Date(start);
          d.setDate(d.getDate() + i);
          events.push({ date: d.toISOString().split('T')[0], type: 'period' });
        }
      }
    }
    return { year, month, events };
  },
  // CACHE
  async cacheCycle(cycle) { if (cycle) await db.cycles.put(cycle); },
  async cacheHistory(records) { if (records?.length) await bulkUpsert(db.cycleHistory, records); },
  async cacheCalendar(data) { /* calendar is derived, no need to cache */ },
};

// ── Contraception ────────────────────────────────────────
export const offlineContraceptionAPI = {
  async get() {
    return db.contraceptions.toArray();
  },
  async create(data) {
    const now = new Date().toISOString();
    await queueOfflineAction({ type: 'POST', endpoint: '/contraception', body: data });
    const id = `local_${Date.now()}`;
    await db.contraceptions.put({ id, ...data, _pendingSync: true, updatedAt: now });
    return { contraception: { id, ...data }, message: 'Ajoutee (hors ligne)' };
  },
  async update(id, data) {
    const now = new Date().toISOString();
    await queueOfflineAction({ type: 'PUT', endpoint: `/contraception/${id}`, body: data });
    await db.contraceptions.update(id, { ...data, updatedAt: now });
    return { contraception: { id, ...data }, message: 'Mise a jour (hors ligne)' };
  },
  async remove(id) {
    await queueOfflineAction({ type: 'DELETE', endpoint: `/contraception/${id}` });
    await db.contraceptions.delete(id);
    return { message: 'Supprimee (hors ligne)' };
  },
  async recordTaken(data) {
    const now = new Date().toISOString();
    await queueOfflineAction({ type: 'POST', endpoint: '/contraception/take', body: data });
    const today = now.split('T')[0];
    await db.contraceptionLogs.put({ id: `local_${Date.now()}`, ...data, date: today, _pendingSync: true, updatedAt: now });
    return { message: 'Prise enregistree (hors ligne)', date: today };
  },
  async getHistory(days = 30) {
    return db.contraceptionLogs.orderBy('date').reverse().limit(days).toArray();
  },
  async getStats() {
    const logs = await db.contraceptionLogs.toArray();
    const totalDays = logs.length;
    const now = Date.now();
    const last30 = logs.filter(l => (now - new Date(l.date).getTime()) < 30 * 86400000);
    const last7 = logs.filter(l => (now - new Date(l.date).getTime()) < 7 * 86400000);
    return {
      totalTrackedDays: totalDays,
      last30DaysAdherence: Math.round((last30.length / 30) * 1000) / 10,
      last7DaysAdherence: Math.round((last7.length / 7) * 1000) / 10,
      currentStreak: 0,
    };
  },
  // CACHE
  async cacheContraceptions(items) { if (items?.length) await bulkUpsert(db.contraceptions, items); },
  async cacheLogs(items) { if (items?.length) await bulkUpsert(db.contraceptionLogs, items); },
};

// ── Notifications ────────────────────────────────────────
export const offlineNotificationsAPI = {
  async getAll() {
    return db.notifications.orderBy('id').reverse().toArray();
  },
  async markAsRead(id) {
    await db.notifications.update(id, { read: true });
    await queueOfflineAction({ type: 'PUT', endpoint: `/notifications/${id}/read` });
    return { id, read: true };
  },
  async remove(id) {
    await db.notifications.delete(id);
    await queueOfflineAction({ type: 'DELETE', endpoint: `/notifications/${id}` });
  },
  // CACHE
  async cacheNotifications(items) {
    if (items?.length) {
      await db.notifications.clear();
      await bulkUpsert(db.notifications, items);
    }
  },
};

// ── Reminders ────────────────────────────────────────────
export const offlineRemindersAPI = {
  async getAll() {
    return db.reminders.toArray();
  },
  async cacheReminders(items) { if (items?.length) await bulkUpsert(db.reminders, items); },
};

// ── Gamification ─────────────────────────────────────────
export const offlineGamificationAPI = {
  async getBadges() { return db.badges.toArray(); },
  async getUserBadges() { return db.badges.toArray(); },
  async getLeaderboard() { return db.leaderboard.orderBy('rank').toArray(); },
  async getChallenges() { return db.weeklyChallenges.toArray(); },
  async getRewards() { return db.rewards.toArray(); },
  // CACHE
  async cacheBadges(items) { if (items?.length) await bulkUpsert(db.badges, items); },
  async cacheLeaderboard(items) {
    if (items?.length) { await db.leaderboard.clear(); await bulkUpsert(db.leaderboard, items); }
  },
  async cacheChallenges(items) {
    if (items?.length) { await db.weeklyChallenges.clear(); await bulkUpsert(db.weeklyChallenges, items); }
  },
  async cacheRewards(items) {
    if (items?.length) { await db.rewards.clear(); await bulkUpsert(db.rewards, items); }
  },
};

// ── Girls Room ───────────────────────────────────────────
export const offlineGirlsRoomAPI = {
  async getEvents(city) {
    if (city) return db.girlsRoomEvents.where('city').equals(city).toArray();
    return db.girlsRoomEvents.toArray();
  },
  async participate(data) {
    await queueOfflineAction({ type: 'POST', endpoint: '/girls-room/participate', body: data });
    return { message: 'Inscription enregistree (hors ligne)' };
  },
  async shareApp(data) {
    await queueOfflineAction({ type: 'POST', endpoint: '/girls-room/share-app', body: data });
    return { message: 'Partage enregistre (hors ligne)' };
  },
  getHelpLines() {
    return [
      { name: 'Ligne Verte Sante Reproductive', number: '800 00 11 22', description: 'Service gratuit du Ministere de la Sante', hours: '24h/24, 7j/7' },
      { name: 'SOS Femmes en Detresse', number: '+225 27 22 44 55 66', description: 'Ecoute et accompagnement', hours: '8h-20h' },
      { name: 'Planning Familial', number: '+225 27 20 33 44 55', description: 'Conseils et orientation', hours: '8h-17h' },
    ];
  },
  async cacheEvents(items) { if (items?.length) await bulkUpsert(db.girlsRoomEvents, items); },
};

// ── Community ────────────────────────────────────────────
export const offlineCommunityAPI = {
  async getQuestions(category) {
    if (category && category !== 'all') return db.communityQuestions.where('category').equals(category).toArray();
    return db.communityQuestions.toArray();
  },
  async postQuestion(data) {
    await queueOfflineAction({ type: 'POST', endpoint: '/community/questions', body: data });
    const id = `local_${Date.now()}`;
    await db.communityQuestions.put({ id, ...data, status: 'pending', answers: [], likes: 0, _pendingSync: true });
    return { message: 'Question soumise (hors ligne)', questionId: id };
  },
  async likeQuestion(id) {
    await queueOfflineAction({ type: 'POST', endpoint: `/community/questions/${id}/like` });
    return { message: 'Like (hors ligne)' };
  },
  async cacheQuestions(items) { if (items?.length) await bulkUpsert(db.communityQuestions, items); },
};

// ── Tips ─────────────────────────────────────────────────
export const offlineTipsAPI = {
  async getAll(category) {
    if (category) return db.tips.where('category').equals(category).toArray();
    return db.tips.toArray();
  },
  async getDaily() {
    const tips = await db.tips.toArray();
    if (!tips.length) return null;
    const today = new Date().toISOString().split('T')[0];
    const hash = [...today].reduce((a, c) => a + c.charCodeAt(0), 0);
    return tips[hash % tips.length];
  },
  async toggleFavorite(tipId) {
    await queueOfflineAction({ type: 'POST', endpoint: `/tips/favorite/${tipId}` });
    const existing = await db.tipFavorites.where('tipId').equals(tipId).first();
    if (existing) {
      await db.tipFavorites.delete(existing.id);
      return { favorited: false };
    }
    await db.tipFavorites.put({ id: `local_${Date.now()}`, tipId, userId: 'current' });
    return { favorited: true };
  },
  async cacheTips(items) { if (items?.length) await bulkUpsert(db.tips, items); },
};

// ── Partners ─────────────────────────────────────────────
export const offlinePartnersAPI = {
  async getAll() { return db.partners.toArray(); },
  async getById(id) { return db.partners.get(id); },
  async cachePartners(items) { if (items?.length) await bulkUpsert(db.partners, items); },
};

// ── Alerts ───────────────────────────────────────────────
export const offlineAlertsAPI = {
  async getSettings() {
    return (await db.alertSettings.toCollection().first()) || null;
  },
  async updateSettings(data) {
    const now = new Date().toISOString();
    await queueOfflineAction({ type: 'PUT', endpoint: '/alerts/settings', body: data });
    const userId = (await db.users.toCollection().first())?.id || 'current';
    await db.alertSettings.put({ userId, ...data, updatedAt: now });
    return { message: 'Parametres mis a jour (hors ligne)' };
  },
  async getCustom() { return db.customAlerts.toArray(); },
  async createCustom(data) {
    const now = new Date().toISOString();
    await queueOfflineAction({ type: 'POST', endpoint: '/alerts/create-custom', body: data });
    const id = `local_${Date.now()}`;
    await db.customAlerts.put({ id, ...data, enabled: true, _pendingSync: true, updatedAt: now });
    return { alert: { id, ...data }, message: 'Alerte creee (hors ligne)' };
  },
  async deleteCustom(id) {
    await queueOfflineAction({ type: 'DELETE', endpoint: `/alerts/custom/${id}` });
    await db.customAlerts.delete(id);
    return { message: 'Supprimee (hors ligne)' };
  },
  async toggleCustom(id) {
    await queueOfflineAction({ type: 'PUT', endpoint: `/alerts/custom/${id}/toggle` });
    const alert = await db.customAlerts.get(id);
    if (alert) await db.customAlerts.update(id, { enabled: !alert.enabled });
    return { enabled: alert ? !alert.enabled : true };
  },
  async cacheSettings(settings) {
    const userId = (await db.users.toCollection().first())?.id || 'current';
    if (settings) await db.alertSettings.put({ userId, ...settings });
  },
  async cacheCustomAlerts(items) { if (items?.length) await bulkUpsert(db.customAlerts, items); },
};

// ── Pending actions count ────────────────────────────────
export async function getPendingCount() { return db.pendingActions.count(); }

// ── Check if we have any local data ──────────────────────
export async function hasLocalData() { return (await db.modules.count()) > 0; }
